import { useStore } from "@nanostores/react";
import { tenantConfig } from "@tenant-config";
import type { PublicConfig } from "@vega-core/appConfigs/PublicConfig.type";
import { $isEmployeeOrg } from "@vega-core/stores/isEmployee.store";

/**
 * Hook to access public configuration
 *
 * Transforms the unified TenantConfig into the legacy PublicConfig format
 * for backward compatibility with existing components.
 */
export function usePublicConfig(): PublicConfig {
  const isEmployee = useStore($isEmployeeOrg);

  return {
    features: {
      footer: {
        links: tenantConfig.links.footer,
        text: tenantConfig.content.footerText,
      },
      registration: {
        links: tenantConfig.links.registration,
        privacyPolicyLink: tenantConfig.links.privacyPolicy,
        welcomeInvestments: tenantConfig.content.welcomeInvestments,
      },
      home: {
        registeredProducts: tenantConfig.content.registeredProducts || [],
      },
    },
    contacts: {
      helpEmail: isEmployee
        ? tenantConfig.contacts.employeeEmail
        : tenantConfig.contacts.investorEmail,
    },
    global: {
      companyId: tenantConfig.metadata.companyId,
      companyName: tenantConfig.metadata.name,
      defaultCurrency: tenantConfig.metadata
        .defaultCurrency as PublicConfig["global"]["defaultCurrency"],
      locale: tenantConfig.metadata.locale,
    },
    theme: {
      favicon: tenantConfig.branding.icons.favicon,
      logos: tenantConfig.branding.logos,
      palette: {
        borderRadius: tenantConfig.branding.borderRadius,
        defaultMode: tenantConfig.branding.defaultMode,
        primaryColor: tenantConfig.branding.brandDefault.light,
      },
      tabTitle:
        tenantConfig.content.tabTitles[
          (import.meta.env.VITE_PORTAL_TYPE ??
            "lp") as keyof typeof tenantConfig.content.tabTitles
        ],
    },
    pwa: {
      icons: {
        "192x192": tenantConfig.branding.icons.icon192,
        "512x512": tenantConfig.branding.icons.icon512,
        appleTouch: tenantConfig.branding.icons.appleTouch,
      },
    },
  };
}
